import React, { useState, useRef, useEffect } from 'react';
import { ParticipantGroup, Participant } from '../types';
import { UsersIcon, MoreHorizontalIcon } from './icons';
import Avatar from './Avatar';

interface GroupCardProps {
  group: ParticipantGroup;
  participants: Participant[];
  onEdit: (group: ParticipantGroup) => void;
  onDelete: (group: ParticipantGroup) => void;
  onClick?: () => void;
}

const GroupCard: React.FC<GroupCardProps> = ({ group, participants, onEdit, onDelete, onClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const groupMembers = participants.filter(p => group.participantIds.includes(p.id));
  const memberCount = groupMembers.length;

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-app-border shadow-sm p-4 flex flex-col transition-all ${onClick ? 'cursor-pointer hover:bg-app-bg' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-action flex items-center justify-center text-white shrink-0">
              <UsersIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-text-ink truncate">{group.name}</h4>
              <p className="text-xs text-text-muted">{memberCount} participants</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" ref={menuRef}>
          {onClick && (
            <svg className="w-5 h-5 text-action" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setIsMenuOpen(!isMenuOpen); }}
            className="p-2 rounded-full text-text-faint hover:bg-neutral-100 active:bg-neutral-200 transition-colors"
          >
            <MoreHorizontalIcon className="w-5 h-5" />
          </button>
          {isMenuOpen && (
            <div className="absolute top-full right-0 mt-1 w-32 bg-white rounded-xl shadow-lg border border-app-border z-10 py-1 overflow-hidden">
              <button onClick={() => { onEdit(group); setIsMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm font-semibold text-text-ink hover:bg-app-bg">Edit</button>
              <button onClick={() => { onDelete(group); setIsMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm font-semibold text-status-warn hover:bg-status-warn-bg">Delete</button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-grow flex items-end">
        {memberCount > 0 ? (
          <div className="flex -space-x-2">
            {groupMembers.slice(0, 5).map((member) => (
              <Avatar
                key={member.id}
                name={member.name}
                avatarUrl={member.avatarUrl}
                id={member.id}
                className="inline-block h-8 w-8 rounded-full ring-2 ring-white object-cover"
              />
            ))}
            {memberCount > 5 && (
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-neutral-200 text-text-muted text-xs font-medium ring-2 ring-white">
                +{memberCount - 5}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-text-faint">No members in this group yet.</p>
        )}
      </div>
    </div>
  );
};

export default GroupCard;
